class ProductVideoGallery extends HTMLElement {
  constructor() {
    super();

    this.elements = {
      yt_player: this.querySelector('#yt-player'),
      yt_player_title: this.querySelector('#yt-player__video-title'),
      yt_player_description: this.querySelector('#yt-player__video-description'),
      yt_videos: this.querySelectorAll('.category-video'),
    };

    this.playerInstance = null;

    this.loadIframeApi = this.loadIframeApi.bind(this);
    this.onYouTubeIframeAPIReady = this.onYouTubeIframeAPIReady.bind(this);
    this.loadVideo = this.loadVideo.bind(this);
    this.scrollToVideoPlayer = this.scrollToVideoPlayer.bind(this);

    Array.from(this.elements.yt_videos).forEach((el) =>
      el.addEventListener('click', this.loadVideo)
    );

    // YouTube API will call this once it's ready
    window.onYouTubeIframeAPIReady = this.onYouTubeIframeAPIReady;

    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (entries[0].isIntersecting) {
          this.loadIframeApi();
          observer.disconnect();
        }
      },
      { rootMargin: '0px 0px 150px 0px' }
    );

    this.observer.observe(this);
  }

  loadIframeApi() {
    // If API already loaded, just init the player
    if (window.YT && window.YT.Player) {
      this.onYouTubeIframeAPIReady();
      return;
    }

    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
  }

  // Helper: set or update iframe title for screen readers
  setIframeTitle(player, title) {
    try {
      const iframe = player.getIframe();
      if (iframe) {
        iframe.setAttribute('title', title || 'Product video');
      }
    } catch (e) {
      // iframe not ready yet – safe to ignore
    }
  }

  onYouTubeIframeAPIReady() {
    const { yt_player, yt_player_title, yt_player_description, yt_videos } = this.elements;
    if (!yt_player) return;

    const fallbackTitle = (yt_player_title && yt_player_title.textContent.trim()) || (yt_videos[0] && yt_videos[0].dataset.title) || yt_player.dataset.title || 'Product video';

    // If there are product videos, load the first one
    if (yt_videos.length > 0) {
      const first = yt_videos[0];

      this.playerInstance = new YT.Player(yt_player, {
        height: '360',
        width: '640',
        videoId: first.dataset.id,
        playerVars: {
          autoplay: '0',
          controls: '2',
          rel: '0',
          showinfo: '0',
        },
        events: {
          onReady: (event) => {
            this.setIframeTitle(event.target, fallbackTitle);
          },
        },
      });

      if (yt_player_title) yt_player_title.textContent = first.dataset.title || '';
      if (yt_player_description) yt_player_description.textContent = first.dataset.description || '';
    } else if (yt_player.dataset.ytid) {
      // Single video case using data-ytid
      const soloTitle = yt_player.dataset.title || fallbackTitle;

      this.playerInstance = new YT.Player(yt_player, {
        height: '360',
        width: '640',
        videoId: yt_player.dataset.ytid,
        playerVars: {
          autoplay: '0',
          controls: '2',
          rel: '0',
          showinfo: '0',
        },
        events: {
          onReady: (event) => {
            this.setIframeTitle(event.target, soloTitle);
          },
        },
      });
    }
  }

  loadVideo(e) {
    e.preventDefault();
    if (!this.playerInstance) return;

    const data = e.currentTarget.dataset;

    this.playerInstance.loadVideoById(data.id, 0, 'large');

    if (this.elements.yt_player_title) {
      this.elements.yt_player_title.textContent = data.title || '';
    }
    if (this.elements.yt_player_description) {
      this.elements.yt_player_description.textContent = data.description || '';
    }

    // Update iframe title so SR users know what video is now playing
    this.setIframeTitle(this.playerInstance, data.title || 'Product video');

    this.scrollToVideoPlayer();
  }

  scrollToVideoPlayer() {
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

customElements.define('product-video-gallery', ProductVideoGallery);
